import {
  Modal, message,
} from '@revfluence/fresh';
import React, {
  memo, useCallback, useEffect, useMemo, useState,
} from 'react';
import {
  capitalize,
  first, isArray, lowerCase, map, uniqBy,
} from 'lodash';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@frontend/shadcn/components/ui/tooltip';
import {
  TooltipHeader,
  TooltipBody,
  TooltipItem,
  TooltipItemValue,
  TooltipArrow,
} from '@frontend/applications/Social/components/tooltip';

import DateFilter from '@frontend/applications/ReportsApp/pages/Reporting/components/DateFilter';
import { dateValues } from '@frontend/applications/ReportsApp/pages/Reporting/components/Header/Header';
import { DateRangesLabel } from '@frontend/app/containers/HomePageMetrics/components/DateSelect';
import _ from 'lodash';
import { InfoCircleTwoTone, SearchOutlined } from '@ant-design/icons';
import { CheckIcon, EllipsisIcon, XmarkIcon } from '@revfluence/fresh-icons/solid/esm';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { DataTable, TablePagination } from '@frontend/app/refresh-components/DataTableV2';
import { ChevronUpIcon, ChevronDownIcon, ArrowDownArrowUpIcon } from '@revfluence/fresh-icons/regular/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@frontend/shadcn/components/ui/select';
import { Input } from '@frontend/shadcn/components/ui/input';
import { TableColumnsIcon } from '@revfluence/fresh-icons/solid/esm';
import { Popover, PopoverContent, PopoverTrigger } from '@frontend/shadcn/components/ui/popover';
import { logger } from '@common';
import Tag from '@frontend/app/refresh-components/Tag';
import dayjs from 'dayjs';
import { getTimeStamp } from '../../utils/dateTimeUtils';
import { OFFER_SOURCE } from '../../types/globalTypes';
import { GetAffiliateConversionHistory_affliateConversionHistory } from '../../queries/types/GetAffiliateConversionHistory';
import { IMemberConversionTable } from '../MemberTable/types';
import { useAcceptRejectConversion } from '../../hooks/acceptAndRejectConversionMutation';
import { DataFormat, formatValue } from '../../utils';
import { CONVERSION_SOURCE, CUSTOMER_TYPE } from '../OfferConversionHistoryDrawer/types';
import { getCustomerType } from '../../utils/getTagType';
import { useGetOfferPayoutLogs } from '../../hooks/useGetOfferPayoutLogs';
import { PayoutCalculationDialog } from '../MemberConversionDrawer/PayoutLogTypes';
import { Checkbox } from '@/shadcn/components/ui/checkbox';
import { getTagColor } from '../../constants/offers';

interface CommissionPopoverContentProps {
  conversionId: number;
  source: OFFER_SOURCE;
  payoutLabel?: string;
  currency: string;
  exchangeRate?: number;
}

const CommissionPopoverContent: React.FC<CommissionPopoverContentProps> = ({
  conversionId,
  source,
  payoutLabel,
  currency,
  exchangeRate,
}) => {
  const { data: payoutData, loading, error } = useGetOfferPayoutLogs(
    conversionId,
    source === OFFER_SOURCE.TUNE ? 'link' : 'promo'
  );

  if (loading) {
    return <div className="p-4 text-center">Loading payout details...</div>;
  }

  if (error) {
    return <div className="p-4 text-center">No Payout Details Available</div>;
  }

  if (!payoutData?.getOfferPayoutLogs?.length) {
    return <div className="p-4 text-center">No payout details available</div>;
  }

  return (
    <PayoutCalculationDialog
      data={payoutData}
      payoutLabel={payoutLabel}
      currency={currency}
      exchangeRate={exchangeRate}
    />
  );
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
const SortButton = ({ column, children }: { column: any, children: React.ReactNode }) => {
  const isSorted = column.getIsSorted();
  const [isHovering, setIsHovering] = React.useState(false);

  const handleSort = () => {
    if (isSorted === 'asc') {
      column.toggleSorting(true);
    } else if (isSorted === 'desc') {
      column.clearSorting();
    } else {
      column.toggleSorting(false);
    }
  };

  return (
    <Button
      variant="ghost"
      className="px-2 hover:bg-transparent w-full"
      onClick={handleSort}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div className="flex w-full justify-between items-center">
        <span>{children}</span>
        <span className="h-3 w-3 flex items-center justify-center">
          {isSorted === 'asc' && <ChevronUpIcon className="h-3 w-3" />}
          {isSorted === 'desc' && <ChevronDownIcon className="h-3 w-3" />}
          {!isSorted && isHovering && <ArrowDownArrowUpIcon className="h-3 w-3" />}
        </span>
      </div>
    </Button>
  );
};

interface IProps {
  affiliateConversionHistory: GetAffiliateConversionHistory_affliateConversionHistory[];
  source: OFFER_SOURCE;
  refetchData: () => void
  refresh: () => void
  payoutLabel?: string
  isLoadingConversionHistory: boolean
}

enum CONVERSION_STATUS {
  ACCEPTED = 'Accepted',
  REJECTED = 'Rejected',
  DUPLICATE = 'Duplicate',
  CANCELED = 'Canceled',
}

const PAGE_SIZE = 10;

export const RefreshMemberConversionTable: React.FC<Readonly<IProps>> = memo((props) => {
  const {
    affiliateConversionHistory, source, refetchData, refresh, payoutLabel,
    isLoadingConversionHistory,
  } = props;

  const [selectedStore, setSelectedStore] = useState<string>('All Stores');
  const [dateRange, setDateRange] = useState<[Date?, Date?]>([null, null]);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [conversionIds, setConversionIds] = useState<(number | string)[]>([]);
  const [conversionType, setConversionType] = useState('');
  const { enableMultipleShopify: isEnabledMultipleShopify, rejectMemberConversions, tieredCommissions, currencyConversionForLink } = useClientFeatures();

  const [sorting, setSorting] = useState([{ id: 'date', desc: true }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [rowSelection, setRowSelection] = useState({});
  const [totalPages, setTotalPages] = useState(1);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const [acceptRejectConversion] = useAcceptRejectConversion({
    onCompleted() {
      message.success(`${conversionIds.length} conversion${conversionIds.length > 1 ? 's' : ''} marked as ${lowerCase(conversionType)}ed`);
      refetchData();
      refresh();
      setConversionIds([]);
    },
  });

  const data = useMemo(() => {
    const [startDate, endDate] = dateRange;
    const filteredAffiliateConversionHistory = affiliateConversionHistory?.filter((affliate) => selectedStore === 'All Stores' || affliate.storeName === selectedStore);
    return map(filteredAffiliateConversionHistory, (conversion) => {
      let dupDiscountCode: string | undefined;
      try {
        if (conversion.discountCode) {
          const duplicateDiscount = JSON.parse(conversion.discountCode);
          if (isArray(duplicateDiscount)) {
            dupDiscountCode = first(duplicateDiscount).code;
          }
        }
      } catch (error) {
        logger.error('Failed to parse conversion discount code:', error);
      }
      const commonFields: IMemberConversionTable = {
        date: conversion.conversionDate,
        _raw: conversion,
        id: String(conversion.conversionId),
        key: String(conversion.conversionId),
        sales: {
          sale: conversion.sale,
          currency: conversion.currency,
        },
        orderId: {
          id: conversion.conversionId,
          url: conversion.orderStatusUrl,
        },
        exchangeRates: {
          rate: conversion.exchangeRate,
          currency: conversion.currency,
        },
        saleAmount: {
          sale: conversion.sale,
          baseSale: conversion.salesBase,
          currency: conversion.currency,
        },
        commissionEarned: conversion.payoutEarned,
        status: conversion.status,
        affiliatInfoOne: conversion.affiliatInfoOne,
        tuneOrderId: conversion.tuneOrderId,
        duplicateDiscountCode: dupDiscountCode,
        source: conversion.source === 'promo' ? CONVERSION_SOURCE.PROMO : CONVERSION_SOURCE.LINK,
        customerType: getCustomerType(conversion?.isNewCustomer),
      };
      return {
        ...commonFields,
      };
    }).filter((conversion) =>
      (startDate ? getTimeStamp(conversion.date) >= getTimeStamp(dayjs(startDate).utc().startOf('day').toDate()) : true)
      && (endDate ? getTimeStamp(conversion.date) <= getTimeStamp(dayjs(endDate).utc().endOf('day').toDate()) : true))
      .filter((conversion) => {
        const searchLower = searchText.toLowerCase();
        const orderId = String(conversion.orderId.id).toLowerCase();
        const tuneOrderId = String(conversion.tuneOrderId || '').toLowerCase();
        return orderId.includes(searchLower) || tuneOrderId.includes(searchLower);
      })
      .sort((a, b) => {
        // Sort by conversion date in descending order (newest first)
        const dateA = new Date(a.date).getTime();
        const dateB = new Date(b.date).getTime();
        return dateB - dateA;
      });
  }, [affiliateConversionHistory, selectedStore, dateRange, searchText]);
  useEffect(() => {
    const selectedRows = data.filter((row) => rowSelection[row.id]);
    setConversionIds(selectedRows.map((row) => (row.source === CONVERSION_SOURCE.LINK ? row.orderId.id : row._raw.id)));
  }, [rowSelection, data]);

  const uniqueStores = useMemo(() => {
    const stores = uniqBy(affiliateConversionHistory, 'storeName').map((obj) => obj.storeName);
    stores.unshift('All Stores');
    return stores;
  }, [affiliateConversionHistory]);

  const handleConversion = useCallback((key) => {
    setConversionType(key);
    const selectedKey = key === 'ACCEPT' ? 'Rejected' : 'Accepted';

    const filterIds = data
      .filter(
        (item) =>
          item.status === selectedKey
          && conversionIds.includes(item.source === CONVERSION_SOURCE.LINK ? item.orderId.id : item._raw.id),
      )
      .map((item) => ({ conversionId: String(item.source === CONVERSION_SOURCE.LINK ? item.orderId.id : item._raw.id), offerSource: item.source === CONVERSION_SOURCE.LINK ? OFFER_SOURCE.TUNE : OFFER_SOURCE.SHOPIFY }));

    Modal.confirm({
      title: `Are you sure you want to ${lowerCase(key)} ${conversionIds.length} conversion${conversionIds.length > 1 ? 's' : ''}?`,
      type: 'warning',
      onOk() {
        message.loading(`${key === 'ACCEPT' ? 'Accepting' : 'Rejecting'} ${conversionIds.length} conversion${conversionIds.length > 1 ? 's' : ''}`, 0);
        if (filterIds.length === 0) {
          message.success(`${conversionIds.length} conversion${conversionIds.length > 1 ? 's' : ''} marked as ${lowerCase(key)}ed`);
          setTimeout(() => {
            message.destroy();
          }, 1500);
          return;
        }
        acceptRejectConversion({
          variables: {
            action: key,
            data: filterIds,
          },
        }).catch((error) => {
          message.error(error.message);
        })
          .finally(() => {
            setTimeout(() => {
              message.destroy();
            }, 1500);
          });
      },
      onCancel() {
      },
      autoFocusButton: null,
      okText: capitalize(key),
    });
  }, [acceptRejectConversion, setConversionType, conversionIds, data]);

  const isUsdStore = _.every(data, (item) => item.saleAmount.currency === 'USD');

  const renderCommissionEarned = useCallback((data: number, record) => {
    if (!tieredCommissions) {
      return <div className="text-right w-full">{`$ ${formatValue(DataFormat.Money, data)}`}</div>;
    }

    return (

      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center gap-2 cursor-pointer">
            {`$ ${formatValue(DataFormat.Money, data)}`}
            <InfoCircleTwoTone />
          </div>
        </TooltipTrigger>
        <TooltipContent
          className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 w-max text-left"
          side="top"
          align="start"
          sideOffset={5}
          role="tooltip"
          aria-live="polite"
        >
          <TooltipHeader>
            Payout Data
          </TooltipHeader>
          <TooltipBody>
            <TooltipItem>
              <TooltipItemValue>
                <CommissionPopoverContent
                  conversionId={Number(record._raw.id)}
                  source={source}
                  payoutLabel={payoutLabel}
                  currency={record.saleAmount.currency}
                  exchangeRate={record.exchangeRates?.rate}
                />
              </TooltipItemValue>
            </TooltipItem>
          </TooltipBody>
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>

    );
  }, [tieredCommissions, source, payoutLabel]);

  const columns = useMemo(() => {
    const baseColumns = [
      {
        id: 'date',
        accessorKey: 'date',
        header: ({ column }) => <SortButton column={column}>Date</SortButton>,
        headerName: 'Date',
        cell: ({ row }) => dayjs(row.original.date).utc().format('MMM DD, YYYY hh:mm A'),
        size: 200,
        minSize: 200,
        maxSize: 200,
        enableSorting: true,
      },
      {
        id: 'orderId',
        accessorKey: 'orderId',
        header: ({ column }) => <SortButton column={column}>Order Id</SortButton>,
        headerName: 'Order Id',
        cell: ({ row }) => (row.original.source === CONVERSION_SOURCE.LINK ?
          row.original.tuneOrderId :
          <a href={row.original.orderId.url} target="_blank" rel="noopener noreferrer">{row.original.orderId.id}</a>),
        size: 200,
        minSize: 200,
        maxSize: 200,
        enableSorting: true,
      },
      {
        id: 'saleAmount',
        accessorKey: 'saleAmount',
        header: ({ column }) => <SortButton column={column}>Sales</SortButton>,
        headerName: 'Sales',
        cell: ({ row }) => (
          <div className="text-right w-full">
            {`${isUsdStore || (row.original.source === CONVERSION_SOURCE.LINK && !currencyConversionForLink) ?
              `$${formatValue(DataFormat.Money, row.original.saleAmount.sale || '-')}` :
              `${formatValue(DataFormat.Money, row.original.saleAmount.baseSale || '-')} ${!isUsdStore ? row.original.saleAmount.currency : ''}`}`}
          </div>
        ),
        size: 200,
        minSize: 200,
        maxSize: 200,
        enableSorting: true,
      },
      {
        id: 'commissionEarned',
        accessorKey: 'commissionEarned',
        header: ({ column }) => <SortButton column={column}>Commission Earned</SortButton>,
        headerName: 'Commission Earned',
        cell: ({ row }) => (
          <div className="text-right w-full">
            {renderCommissionEarned(row.original.commissionEarned, row.original)}
          </div>
        ),
        size: 170,
        minSize: 170,
        maxSize: 170,
        enableSorting: true,
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: ({ column }) => (
          <div className="flex items-center gap-1">
            <SortButton column={column}>Status</SortButton>
            <Tooltip>
              <TooltipTrigger asChild>
                <InfoCircleTwoTone />
              </TooltipTrigger>
              <TooltipContent
                className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
                side="bottom"
                align="start"
                sideOffset={5}
                role="tooltip"
                aria-live="polite"
              >
                <TooltipHeader />
                <TooltipBody>
                  <TooltipItem>
                    <TooltipItemValue>
                      By default, all conversions are marked as accepted. You can reject/accept a conversion until it has been paid.
                    </TooltipItemValue>
                  </TooltipItem>
                </TooltipBody>
                <TooltipArrow />
              </TooltipContent>
            </Tooltip>
          </div>
        ),
        headerName: 'Status',
        cell: ({ row }) => {
          if (row.original.duplicateDiscountCode) {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Tag bgColor={getTagColor(CONVERSION_STATUS.DUPLICATE)} textColor="text-primary" label={CONVERSION_STATUS.DUPLICATE} />
                  </div>
                </TooltipTrigger>
                <TooltipContent
                  className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium z-50 max-w-[200px]"
                  side="bottom"
                  align="start"
                  sideOffset={5}
                  role="tooltip"
                  aria-live="polite"
                >
                  <TooltipHeader />
                  <TooltipBody>
                    <TooltipItem>
                      <TooltipItemValue>
                        {`Conversion duplicated by promo ${row.original.duplicateDiscountCode}`}
                      </TooltipItemValue>
                    </TooltipItem>
                  </TooltipBody>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
            );
          }
          return <Tag bgColor={getTagColor(row.original.status)} textColor="text-primary" label={row.original.status} />;
        },
        size: 120,
        minSize: 120,
        maxSize: 120,
        enableSorting: true,
        filterFn: (row, value) => row.original.status === value,
      },
    ];

    if ((source === OFFER_SOURCE.TUNE ? currencyConversionForLink : true) && !isUsdStore) {
      baseColumns.push(
        {
          id: 'exchangeRates',
          accessorKey: 'exchangeRates',
          header: ({ column }) => <SortButton column={column}>Exchange Rate(USD)</SortButton>,
          headerName: 'Exchange Rate(USD)',
          cell: ({ row }) => (row.original.source === CONVERSION_SOURCE.PROMO || currencyConversionForLink ? (
            <Tag bgColor="bg-grey-2" textColor="text-primary" label={`1 ${row.original.exchangeRates.currency} = ${Number(row.original.exchangeRates.rate).toFixed(4)} USD`} />
          ) : '--'),
          size: 170,
          minSize: 170,
          maxSize: 170,
          enableSorting: true,
        },
        {
          id: 'sales',
          accessorKey: 'sales',
          header: ({ column }) => <SortButton column={column}>Sales (USD)</SortButton>,
          headerName: 'Sales (USD)',
          cell: ({ row }) => (
            <div className="text-right w-full">
              {(row.original.source === CONVERSION_SOURCE.PROMO || currencyConversionForLink) ?
                `$ ${formatValue(DataFormat.Money, row.original.sales.sale)}` : '--'}
            </div>
          ),
          size: 150,
          minSize: 150,
          maxSize: 150,
          enableSorting: true,
        },
      );
    }

    if (source === OFFER_SOURCE.PROMO_LINK) {
      baseColumns.push({
        id: 'source',
        accessorKey: 'source',
        header: ({ column }) => <SortButton column={column}>Source</SortButton>,
        headerName: 'Source',
        cell: ({ row }) => <Tag bgColor={row.original.source === CONVERSION_SOURCE.LINK ? 'bg-blue-2' : 'bg-purple-2'} textColor="text-primary" label={row.original.source} />,
        size: 120,
        minSize: 120,
        maxSize: 120,
        enableSorting: true,
      });
    }

    if (source === OFFER_SOURCE.SHOPIFY || source === OFFER_SOURCE.PROMO_LINK) {
      baseColumns.push({
        id: 'customerType',
        accessorKey: 'customerType',
        header: ({ column }) => <SortButton column={column}>Customer</SortButton>,
        headerName: 'Customer',
        cell: ({ row }) => (row.original.customerType ?
          <Tag bgColor={row.original.customerType === CUSTOMER_TYPE.NEW_CUSTOMER ? 'bg-blue-2' : 'bg-purple-2'} textColor="text-primary" label={row.original.customerType} /> : '-'),
        size: 120,
        minSize: 120,
        maxSize: 120,
        enableSorting: true,
      });
    }

    return baseColumns;
  }, [source, isUsdStore, currencyConversionForLink, renderCommissionEarned]);

  const handleSortingChange = useCallback((newSorting) => {
    setSorting(newSorting);
    setCurrentPage(1);
  }, []);

  const handleStoreChange = useCallback((value: string) => {
    setSelectedStore(value);
    setCurrentPage(1);
  }, []);

  const filteredDataByStore = useMemo(() => {
    let filtered = selectedStore === 'All Stores' ? data : data.filter((item) => item._raw.storeName === selectedStore);

    if (sorting.length > 0) {
      const [{ id, desc }] = sorting;
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[id as keyof typeof a];
        let bValue = b[id as keyof typeof b];

        if (id === 'saleAmount') {
          aValue = a.saleAmount.sale;
          bValue = b.saleAmount.sale;
        } else if (id === 'orderId') {
          aValue = a.source === CONVERSION_SOURCE.LINK ? a.tuneOrderId : a.orderId.id;
          bValue = b.source === CONVERSION_SOURCE.LINK ? b.tuneOrderId : b.orderId.id;
        } else if (id === 'exchangeRates') {
          aValue = a.exchangeRates?.rate;
          bValue = b.exchangeRates?.rate;
        } else if (id === 'sales') {
          aValue = a.sales?.sale;
          bValue = b.sales?.sale;
        }

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        if (id === 'date') {
          const dateA = new Date(a.date).getTime();
          const dateB = new Date(b.date).getTime();
          return desc ? dateB - dateA : dateA - dateB;
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
          return desc ? bValue - aValue : aValue - bValue;
        }

        const strA = String(aValue).toLowerCase();
        const strB = String(bValue).toLowerCase();
        return desc ? strB.localeCompare(strA) : strA.localeCompare(strB);
      });
    }

    return filtered;
  }, [data, selectedStore, sorting]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * PAGE_SIZE;
    const endIndex = startIndex + PAGE_SIZE;
    return filteredDataByStore.slice(startIndex, endIndex);
  }, [filteredDataByStore, currentPage]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredDataByStore.length / PAGE_SIZE));
  }, [filteredDataByStore]);

  return (
    <>
      <p className="text-sm text-grey-6">
        View a comprehensive record of all conversions for this member.
      </p>
      <div className="flex flex-col gap-4">
        <div className="flex gap-3">
          {(isEnabledMultipleShopify && uniqueStores.length > 2) && (
            <div className="flex-1">
              <Select
                defaultValue={selectedStore}
                onValueChange={handleStoreChange}
              >
                <SelectTrigger data-testid="store-select-trigger">
                  <SelectValue placeholder="Select Store" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  {uniqueStores.map((store, index) => (
                    <SelectItem key={index} value={store || 'all-stores'}>
                      {store}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="flex-1">
            <DateFilter
              key="date-filter"
              defaultLabel={DateRangesLabel.ALL_TIME}
              isCheckbox={false}
              options={dateValues}
              isCustomDate={false}
              onFilterChange={([filter]) => filter && setDateRange(filter.value.dateRange)}
              refreshUi
              textColor="text-primary"
            />
          </div>
        </div>
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <p className="text-sm text-primary m-0">{paginatedData.length} of {filteredDataByStore.length} conversions</p>
            {rejectMemberConversions && (
              <Select
                value=""
                onValueChange={(value) => handleConversion(value)}
              >
                <SelectTrigger data-testid="action-select-trigger" showIcon={false} className="p-0 h-8 w-8 bg-transparent border-0 shadow-none flex items-center justify-center hover:bg-muted">
                  <EllipsisIcon className="h-5 w-5 text-primary" />
                </SelectTrigger>
                <SelectContent className="z-[9999]">
                  <SelectItem value="ACCEPT" disabled={conversionIds.length === 0}>
                    <div className="flex items-center gap-2">
                      <CheckIcon className="h-4 w-4" />
                      Accept Conversion
                    </div>
                  </SelectItem>
                  <SelectItem value="REJECT" disabled={conversionIds.length === 0}>
                    <div className="flex items-center gap-2 text-destructive">
                      <XmarkIcon className="h-4 w-4" />
                      Reject Conversion
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex items-center">
              <Button
                variant="ghost"
                className="w-8 h-8 flex items-center justify-center z-20"
                size="icon"
                onClick={() => setIsSearchOpen((v) => !v)}
                aria-label="Search"
              >
                {isSearchOpen ? (
                  <XmarkIcon className="text-muted-foreground" />
                ) : (
                  <SearchOutlined className="text-muted-foreground" />
                )}
              </Button>
              <div
                className={`absolute right-10 top-0 transition-all duration-300 ${isSearchOpen ? 'opacity-100 w-48' : 'opacity-0 w-0'} overflow-hidden`}
                style={{ zIndex: 10 }}
              >
                <Input
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  type="search"
                  placeholder="Search for order Id"
                  className="rounded-lg bg-grey-0 pl-8 w-48 focus:ring-0 focus-visible:ring-0 outline-none"
                  autoFocus={isSearchOpen}
                />
                <SearchOutlined className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              </div>
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Toggle column visibility"
                  className="relative z-50"
                >
                  <TableColumnsIcon />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-56 p-4 z-[9999] relative select-text pointer-events-auto">
                <div className="flex flex-col gap-2">
                  <h4 className="font-medium mb-2">Visible Columns</h4>
                  {columns.map((column) => (
                    <label key={column.id} className="flex items-center gap-2">
                      <Checkbox
                        checked={columnVisibility[column.id] !== false}
                        onCheckedChange={(checked) => {
                          setColumnVisibility((prev) => ({
                            ...prev,
                            [column.id]: checked === true,
                          }));
                        }}
                      />
                      <span>{column.headerName}</span>
                    </label>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <TablePagination
              pageIndex={currentPage - 1}
              pageSize={PAGE_SIZE}
              totalCount={filteredDataByStore.length}
              canPreviousPage={currentPage > 1}
              canNextPage={currentPage < totalPages}
              onPaginationChange={(newPage) => setCurrentPage(newPage + 1)}
            />
          </div>
        </div>
        <div className="overflow-y-auto border-t border-l border-r rounded-xl">
          <DataTable
            columns={columns}
            data={paginatedData}
            enableColumnResizing
            getRowId={(row) => row.id.toString()}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onSortingChange={handleSortingChange}
            enableRowSelection={rejectMemberConversions}
            rowSelection={rowSelection}
            onRowSelectionChange={setRowSelection}
            isLoading={isLoadingConversionHistory}
            pinnedLeft={['date']}
          />
        </div>
      </div>
    </>
  );
});
